import React from 'react';
import { Check, Shield, Clock, DollarSign, Home, Building, Zap, Wind, Bug, Flame, Recycle } from 'lucide-react';

const InfoPageContent = () => {
  return (
    <section id="info-page" className="py-16 md:py-20 bg-slate-950" data-testid="info-page-content">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1
            className="text-4xl md:text-5xl font-bold tracking-tight mb-6 uppercase text-orange-500"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            About 3D Metal Home Building
          </h1>
          <p className="text-xl md:text-2xl text-white max-w-4xl mx-auto leading-relaxed">
            Texas's Premier Light Gauge Steel Construction Company — Building Stronger, Faster, and Smarter
          </p>
        </div>

        {/* Company Overview */}
        <div className="glass-card p-8 md:p-10 mb-12">
          <div className="max-w-4xl mx-auto">
            <h2
              className="text-3xl font-semibold mb-6 text-orange-500 text-center"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              Who We Are
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-6">
              <span className="text-orange-500 font-bold">3D Metal Home Building</span> is a Dallas-based steel construction company 
              specializing in light gauge steel (LGS) framing for residential and commercial buildings. Founded by 
              <span className="text-white font-semibold"> President Toby Watson</span>, we've revolutionized the way Texas builds — 
              delivering homes and commercial structures that are stronger, faster to construct, and built to last generations.
            </p>
            <p className="text-lg text-slate-300 leading-relaxed mb-6">
              Unlike traditional wood framing that's vulnerable to termites, fire, mold, and warping, our cold-formed steel 
              construction provides a superior alternative that saves you money on insurance, maintenance, and energy costs 
              for decades to come.
            </p>
            <p className="text-lg text-slate-300 leading-relaxed">
              From custom homes to commercial buildings, barndominiums to multi-story structures — we deliver precision-engineered 
              steel frames that go from blueprint to fully framed in as little as <span className="text-orange-500 font-bold">5 days</span>.
            </p>
          </div>
        </div>

        {/* Why Steel Section */}
        <div className="mb-16">
          <h2
            className="text-3xl md:text-4xl font-semibold mb-10 text-orange-500 text-center"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            Why Choose Steel Over Wood?
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="glass-card p-6 text-center">
              <Flame className="text-orange-500 mx-auto mb-4" size={48} />
              <h3 className="text-xl font-bold text-white mb-3">Fire Resistant</h3>
              <p className="text-slate-300">Steel doesn't burn. Our non-combustible frames provide superior fire protection, often reducing insurance premiums by 15-40%.</p>
            </div>
            
            <div className="glass-card p-6 text-center">
              <Bug className="text-orange-500 mx-auto mb-4" size={48} />
              <h3 className="text-xl font-bold text-white mb-3">Termite Proof</h3>
              <p className="text-slate-300">Say goodbye to pest damage forever. Steel is 100% immune to termites, carpenter ants, and wood-boring insects.</p>
            </div>
            
            <div className="glass-card p-6 text-center">
              <Wind className="text-orange-500 mx-auto mb-4" size={48} />
              <h3 className="text-xl font-bold text-white mb-3">Storm Strong</h3>
              <p className="text-slate-300">Engineered to withstand 180+ mph winds. Our steel frames protect your family during Texas storms and hurricanes.</p>
            </div>
            
            <div className="glass-card p-6 text-center">
              <Shield className="text-orange-500 mx-auto mb-4" size={48} />
              <h3 className="text-xl font-bold text-white mb-3">Mold & Moisture Resistant</h3>
              <p className="text-slate-300">Steel doesn't absorb moisture, rot, or support mold growth — keeping your indoor air quality healthy for decades.</p>
            </div>
            
            <div className="glass-card p-6 text-center">
              <Clock className="text-orange-500 mx-auto mb-4" size={48} />
              <h3 className="text-xl font-bold text-white mb-3">50+ Year Lifespan</h3>
              <p className="text-slate-300">Galvanized steel frames last generations without warping, twisting, or structural degradation like wood.</p>
            </div>
            
            <div className="glass-card p-6 text-center">
              <Recycle className="text-orange-500 mx-auto mb-4" size={48} />
              <h3 className="text-xl font-bold text-white mb-3">100% Recyclable</h3>
              <p className="text-slate-300">Steel is the most recycled material on Earth. Zero waste construction that's better for Texas and the planet.</p>
            </div>
          </div>
        </div>

        {/* Our Process */}
        <div className="glass-card p-8 md:p-10 mb-16">
          <h2
            className="text-3xl md:text-4xl font-semibold mb-8 text-orange-500 text-center"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            Our 5-Day Framing Process
          </h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-orange-500 text-white font-bold rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0">1</div>
                <div>
                  <h3 className="text-xl font-bold text-white">Day 1: Precision Manufacturing</h3>
                  <p className="text-slate-300">Once we receive your approved plans, we manufacture your custom steel frames using computer-controlled precision equipment.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="bg-orange-500 text-white font-bold rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0">2</div>
                <div>
                  <h3 className="text-xl font-bold text-white">Day 2: Assembly</h3>
                  <p className="text-slate-300">All frames are assembled into sections at our facility, ensuring perfect fit and faster on-site installation.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="bg-orange-500 text-white font-bold rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0">3</div>
                <div>
                  <h3 className="text-xl font-bold text-white">Day 3: Delivery</h3>
                  <p className="text-slate-300">Your complete steel frame kit is delivered directly to your job site, ready for installation.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="bg-orange-500 text-white font-bold rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0">4-5</div>
                <div>
                  <h3 className="text-xl font-bold text-white">Days 4-5: On-Site Framing</h3>
                  <p className="text-slate-300">Our expert crews complete full framing in just 2-3 days — that's weeks faster than traditional wood framing.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cost Savings */}
        <div className="mb-16">
          <h2
            className="text-3xl md:text-4xl font-semibold mb-8 text-orange-500 text-center"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            Real Cost Savings
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="glass-card p-6 text-center">
              <DollarSign className="text-green-500 mx-auto mb-3" size={40} />
              <p className="text-3xl font-bold text-green-500 mb-2">10-20%</p>
              <p className="text-white font-semibold">Total Construction Savings</p>
              <p className="text-slate-400 text-base">vs. wood framing</p>
            </div>
            
            <div className="glass-card p-6 text-center">
              <Shield className="text-green-500 mx-auto mb-3" size={40} />
              <p className="text-3xl font-bold text-green-500 mb-2">15-40%</p>
              <p className="text-white font-semibold">Insurance Savings</p>
              <p className="text-slate-400 text-base">lower premiums</p>
            </div>
            
            <div className="glass-card p-6 text-center">
              <Recycle className="text-green-500 mx-auto mb-3" size={40} />
              <p className="text-3xl font-bold text-green-500 mb-2">80%</p>
              <p className="text-white font-semibold">Less Waste</p>
              <p className="text-slate-400 text-base">reduced trash haul</p>
            </div>
            
            <div className="glass-card p-6 text-center">
              <Zap className="text-green-500 mx-auto mb-3" size={40} />
              <p className="text-3xl font-bold text-green-500 mb-2">75%</p>
              <p className="text-white font-semibold">Energy Savings</p>
              <p className="text-slate-400 text-base">with proper insulation</p>
            </div>
          </div>
        </div>

        {/* What We Build */}
        <div className="glass-card p-8 md:p-10 mb-16">
          <h2
            className="text-3xl md:text-4xl font-semibold mb-8 text-orange-500 text-center"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            What We Build
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div>
              <div className="flex items-center mb-4">
                <Home className="text-orange-500 mr-3" size={32} />
                <h3 className="text-2xl font-bold text-white">Residential</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <Check className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-300">Custom Steel Frame Homes</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-300">Barndominiums</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-300">Multi-Story Residences</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-300">Garage & Workshop Additions</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-300">ADUs & Guest Houses</span>
                </li>
              </ul>
            </div>
            
            <div>
              <div className="flex items-center mb-4">
                <Building className="text-orange-500 mr-3" size={32} />
                <h3 className="text-2xl font-bold text-white">Commercial</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <Check className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-300">Office Buildings</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-300">Retail Spaces</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-300">Warehouses & Storage</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-300">Agricultural Buildings</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <span className="text-slate-300">Churches & Community Centers</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Steel vs Wood Comparison */}
        <div className="mb-16">
          <h2
            className="text-3xl md:text-4xl font-semibold mb-8 text-orange-500 text-center"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            Steel vs. Wood: The Clear Winner
          </h2>
          
          <div className="max-w-4xl mx-auto overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="py-4 px-4 text-white font-bold">Feature</th>
                  <th className="py-4 px-4 text-orange-500 font-bold">3D Metal Steel</th>
                  <th className="py-4 px-4 text-slate-400 font-bold">Traditional Wood</th>
                </tr>
              </thead>
              <tbody className="text-slate-300">
                <tr className="border-b border-slate-800">
                  <td className="py-4 px-4">Fire Resistance</td>
                  <td className="py-4 px-4 text-green-500 font-semibold">Non-Combustible</td>
                  <td className="py-4 px-4 text-red-400">Highly Flammable</td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="py-4 px-4">Termites</td>
                  <td className="py-4 px-4 text-green-500 font-semibold">100% Immune</td>
                  <td className="py-4 px-4 text-red-400">Highly Vulnerable</td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="py-4 px-4">Mold & Rot</td>
                  <td className="py-4 px-4 text-green-500 font-semibold">Cannot Occur</td>
                  <td className="py-4 px-4 text-red-400">Common Problem</td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="py-4 px-4">Warping/Twisting</td>
                  <td className="py-4 px-4 text-green-500 font-semibold">Never</td>
                  <td className="py-4 px-4 text-red-400">Frequent</td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="py-4 px-4">Framing Time</td>
                  <td className="py-4 px-4 text-green-500 font-semibold">2-3 Days</td>
                  <td className="py-4 px-4 text-red-400">2-4 Weeks</td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="py-4 px-4">Lifespan</td>
                  <td className="py-4 px-4 text-green-500 font-semibold">50+ Years</td>
                  <td className="py-4 px-4 text-red-400">20-30 Years</td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="py-4 px-4">Insurance Costs</td>
                  <td className="py-4 px-4 text-green-500 font-semibold">15-40% Lower</td>
                  <td className="py-4 px-4 text-red-400">Standard/Higher</td>
                </tr>
                <tr>
                  <td className="py-4 px-4">Recyclable</td>
                  <td className="py-4 px-4 text-green-500 font-semibold">100%</td>
                  <td className="py-4 px-4 text-red-400">Limited</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </section>
  );
};

export default InfoPageContent;
